import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { NgMaterialModule } from '@principle-theorem/ng-material';
import {
  formControlChanges$,
  isDisabled$,
  NgSharedModule,
  TypedFormControl,
  TypedFormGroup,
} from '@principle-theorem/ng-shared';
import { MohAgreementHolder } from '@principle-theorem/principle-core';
import {
  DEFAULT_MOH_CLAIM_EMAIL,
  DEFAULT_MOH_ENROLMENT_EMAIL,
  IPractice,
  IPracticeMohSettings,
  MOH_AGREEMENT_HOLDER_SOURCE_LABEL_MAP,
  MohAgreementHolderSource,
  NZ_HEALTH_DISTRICTS,
} from '@principle-theorem/principle-core/interfaces';
import { Firestore, getEnumValues, WithRef } from '@principle-theorem/shared';
import { Observable } from 'rxjs';

@Component({
  selector: 'pr-practice-moh-settings',
  imports: [NgMaterialModule, ReactiveFormsModule, NgSharedModule],
  templateUrl: './practice-moh-settings.component.html',
  styleUrl: './practice-moh-settings.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PracticeMohSettingsComponent {
  private _snackBar = inject(MatSnackBar);
  practice = input.required<WithRef<IPractice>>();
  formSaved = output();
  mohSettings = computed(() => this.practice().mohSettings);
  saveDisabled$: Observable<boolean>;
  emailFieldDisabled$: Observable<boolean>;
  districts = NZ_HEALTH_DISTRICTS;
  agreementHolderSources = getEnumValues(MohAgreementHolderSource);
  agreementHolderSourceLabels = MOH_AGREEMENT_HOLDER_SOURCE_LABEL_MAP;
  claimsUnderPractice = signal(false);

  form = new TypedFormGroup<IPracticeMohSettings>({
    agreementNumber: new TypedFormControl<string>('', Validators.maxLength(6)),
    payeeNumber: new TypedFormControl<string>('', Validators.maxLength(6)),
    agreementHolderName: new TypedFormControl<string>(''),
    agreementHolderSource: new TypedFormControl<MohAgreementHolderSource>(
      MohAgreementHolderSource.Practitioner
    ),
    claimFormEmail: new TypedFormControl<string>(DEFAULT_MOH_CLAIM_EMAIL, [
      Validators.email,
      Validators.required,
    ]),
    preApprovalFormEmail: new TypedFormControl<string>('', [
      Validators.email,
      Validators.required,
    ]),
    enrolmentFormEmail: new TypedFormControl<string>(
      DEFAULT_MOH_ENROLMENT_EMAIL,
      Validators.email
    ),
    ccEmail: new TypedFormControl<string>('', [Validators.email]),
    bccEmail: new TypedFormControl<string>('', [Validators.email]),
    testModeEnabled: new TypedFormControl<boolean>(false),
    testEmail: new TypedFormControl<string>('', Validators.email),
    districtArea: new TypedFormControl<string>(''),
  });

  constructor() {
    effect(() => {
      const mohSettings = this.mohSettings();
      if (!mohSettings) {
        return;
      }
      this.form.patchValue(mohSettings);
    });

    this.saveDisabled$ = isDisabled$(this.form);

    formControlChanges$(this.form.controls.testModeEnabled)
      .pipe(takeUntilDestroyed())
      .subscribe((testModeEnabled) => {
        if (testModeEnabled) {
          this.form.controls.testEmail.enable();
          this.form.controls.testEmail.addValidators(Validators.required);
        } else {
          this.form.controls.testEmail.disable();
          this.form.controls.testEmail.removeValidators(Validators.required);
        }
        this.form.controls.testEmail.updateValueAndValidity();
      });

    formControlChanges$(this.form.controls.agreementHolderSource)
      .pipe(takeUntilDestroyed())
      .subscribe(() =>
        this.claimsUnderPractice.set(
          MohAgreementHolder.claimsUnderPractice({
            mohSettings: this.form.getRawValue(),
          })
        )
      );
  }

  async save(): Promise<void> {
    if (!this.form.valid) {
      return;
    }
    const formData = this.form.getRawValue();
    await Firestore.patchDoc(this.practice().ref, {
      mohSettings: formData,
    });
    this._snackBar.open('MOH Settings Saved');
    this.formSaved.emit();
  }
}
